import { Button, alpha, styled } from '@mui/material';
import cn from 'classnames';
import { ReactElement } from 'react';
import { ActionButton } from 'src/components/ActionButton/ActionButton';
import { ActionButtonProps } from 'src/components/ActionButton/types';
import { PopoverProps, PopoverTrigger } from 'src/components/PopoverTrigger';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';

type Props<P extends object> = ActionButtonProps & {
  popover: (props: PopoverProps<P>) => ReactElement;
  context?: P | undefined;
  hasPermission?: boolean;
  askPermission?: () => void;
  hideActions?: boolean;
};

export const SettingsActionButton = <P extends object>({
  children,
  popover,
  context,
  color,
  tooltip,
  onClick,
  hasPermission = true,
  askPermission,
  hideActions = false,
  className,
}: Props<P>) => {
  const { translate } = useLocales();
  return (
    <StyledContainer
      className={cn(className, 'options-button', {
        hasPermission,
        hideActions,
      })}
    >
      {!hideActions && (
        <>
          {hasPermission ? (
            <PopoverTrigger<P> popover={popover} context={context}>
              <StyledButton
                size="small"
                variant="text"
                disableElevation
                className="popover-button"
              >
                <Iconify icon="tabler:chevron-up" />
              </StyledButton>
            </PopoverTrigger>
          ) : (
            <StyledButton
              size="small"
              variant="text"
              disableElevation
              className="popover-button"
              disabled
            >
              <Iconify icon="tabler:alert-triangle-filled" color="error.main" />
            </StyledButton>
          )}
        </>
      )}
      <ActionButton
        onClick={hasPermission ? onClick : askPermission}
        color={color}
        tooltip={
          hasPermission
            ? tooltip
            : translate('shared.labels.permission_required')
        }
      >
        {children}
      </ActionButton>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  '&:not(.hideActions)': {
    backgroundColor: alpha(theme.palette.error.main, 0.16),
    '&.hasPermission': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: 26,
  height: 40,
  minWidth: 'unset',
  svg: {
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
  },
  borderRadius: theme.spacing(1, 0, 0, 1),
  '&:hover': {
    boxShadow: 'none',
    backgroundColor: 'inherit',
    svg: {
      color: theme.palette.text.primary,
    },
  },
  '&.popover-button': {
    display: 'none',
  },
  '&.Mui-disabled': {
    svg: {
      color: theme.palette.warning.main,
    },
  },
  [theme.breakpoints.up('md')]: {
    '&.popover-button': {
      display: 'inline-flex',
    },
  },
}));
